import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import Board from './Board';
import CardDetails from './CardDetails';

import './scss/index.scss';
import { useBoard } from '../../context/BoardContext';
import useBoardPermissions from '../../hooks/useBoardPermissions';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import { updateBoardMembers } from '../../redux/Slices/boardSlice';
import { fetchMembers } from '../../redux/Slices/memberSlice';

function BoardWrapper() {
    const dispatch = useDispatch();
    const location = useLocation();
    const isCardPath = location.pathname.startsWith('/c/');

    const { boardState, setBoard } = useBoard();
    const { board, isLoading, selectedBackgroundUrl } = useSelector((state) => state.board);
    const { targetListId, targetPosition, card, cardAction } = useSelector((state) => state.moveCard);
    const user = useSelector((state) => state.user);

    // Get board permissions
    const boardPermissions = useBoardPermissions(boardState);

    // NOTE: BOARD_UPDATED is handled in Board.js to avoid duplicate updates
    // Real-time socket listener for BOARD member changes (to update permissions)
    // NOTE: This should NOT handle card member events (which also use MEMBER_ADDED/MEMBER_REMOVED)
    useSocketAction([
        ActionTypes.MEMBER_ADDED,
        ActionTypes.MEMBER_REMOVED,
        ActionTypes.ROLE_UPDATED,
    ], async (data) => {
        // Skip events triggered by current user (already handled optimistically)
        if (data.reqUser === user?.user?._id) return;

        // IMPORTANT: Only process BOARD member events, not CARD member events
        // Card member events have a 'card' property, board member events have 'typeId' or 'members'
        if (data.card) {
            // This is a card member event, ignore it
            return;
        }

        // Only process events for the current board
        if ((data.typeId !== board?.shortId) && (data.boardId !== board?.shortId)) return;

        console.log('[BoardWrapper] Real-time BOARD member event:', data.action, data);

        // Use members from socket event if available, otherwise fetch
        let updatedMembers = data.members;
        if (!updatedMembers) {
            updatedMembers = await dispatch(fetchMembers({ type: 'board', id: board.shortId })).unwrap();
        }

        console.log('[BoardWrapper] Updated members structure:', updatedMembers?.[0]);

        // Update Redux board state with new members
        dispatch(updateBoardMembers(updatedMembers));

        // Update context board state with new members to trigger permission recalculation
        // IMPORTANT: Ensure we maintain the board object structure to prevent read-only mode flickering
        if (updatedMembers && Array.isArray(updatedMembers)) {
            setBoard((prevBoard) => {
                if (!prevBoard) return prevBoard;

                return {
                    ...prevBoard,
                    members: updatedMembers
                };
            });
        }
    }, [dispatch, board?.shortId, user?.user?._id, setBoard]);


    // Sync board background to app main or card container so the collapsed sidebar can share it
    useEffect(() => {
        const applyBackgroundVar = (el) => {
            if (!el) return;
            const url = selectedBackgroundUrl || board?.cover?.url;
            const bgUrl = (isLoading || !url)
                ? ''
                : `url('${url}')`;

            if (bgUrl) {
                el.style.setProperty('--background-url', bgUrl);
            } else {
                el.style.removeProperty('--background-url');
            }
        };

        // Apply to both board and card pages to persist background when viewing card details
        applyBackgroundVar(document.getElementById('board-page'));
        applyBackgroundVar(document.getElementById('card-page'));

        return () => {
            // Clean up when unmounting
            const boardEl = document.getElementById('board-page');
            const cardEl = document.getElementById('card-page');
            boardEl?.style.removeProperty('--background-url');
            cardEl?.style.removeProperty('--background-url');
        };
    }, [isLoading, selectedBackgroundUrl, board?.cover?.url, location.pathname]);

    // Sync Redux board state to context (for optimistic updates)
    // BUT preserve cards from context since Redux may not have all cards loaded
    // IMPORTANT: Only sync when actionLists actually change, not when board properties change
    // Use a ref to track previous actionLists to avoid unnecessary updates
    const prevActionListsRef = useRef(null);
    const prevBoardRef = useRef(null);

    useEffect(() => {
        if (board && board.actionLists) {
            // Create a stable reference for comparison
            const currentListIds = board.actionLists.map(l => l._id).sort().join(',');

            // Skip if actionLists haven't changed
            if (prevActionListsRef.current === currentListIds) {
                console.log('[BoardWrapper] Skipping context update - actionLists unchanged');

                // However, still update board properties (title, visibility, etc.) if they changed
                if (prevBoardRef.current && (
                    prevBoardRef.current.title !== board.title ||
                    prevBoardRef.current.visibility !== board.visibility ||
                    prevBoardRef.current.description !== board.description
                )) {
                    console.log('[BoardWrapper] Updating board properties only (not actionLists)');
                    setBoard((prevBoard) => ({
                        ...prevBoard,
                        title: board.title,
                        visibility: board.visibility,
                        description: board.description,
                        cover: board.cover,
                        background: board.background,
                        members: board.members,
                        workspace: board.workspace,
                        wShortId: board.wShortId,
                        shortId: board.shortId,
                        _id: board._id,
                        // Don't update actionLists
                    }));
                }

                prevBoardRef.current = board;
                return;
            }

            prevActionListsRef.current = currentListIds;
            prevBoardRef.current = board;
            console.log('[BoardWrapper] Syncing Redux board to context - actionLists changed');

            setBoard((prevBoard) => {
                if (!prevBoard || !prevBoard.actionLists) {
                    // If no previous board, just use Redux state
                    return board;
                }

                // Merge Redux list properties with context cards
                const mergedLists = board.actionLists.map((reduxList) => {
                    // Find corresponding list in context
                    const contextList = prevBoard.actionLists.find(
                        (list) => list._id === reduxList._id || list.shortId === reduxList.shortId
                    );

                    // If list exists in context, preserve its cards
                    if (contextList) {
                        return {
                            ...reduxList, // Use Redux list properties (order, title, color, etc.)
                            cards: contextList.cards || reduxList.cards || [] // Preserve context cards
                        };
                    }

                    // New list from Redux, use as-is
                    return reduxList;
                });

                return { ...board, actionLists: mergedLists };
            });
        }
    }, [board, setBoard]);

    useEffect(() => {
        if (!card || !cardAction) return;

        setBoard((prevBoard) => {
            // Safety check: ensure prevBoard and actionLists exist
            if (!prevBoard || !prevBoard.actionLists) return prevBoard;

            let cardToProcess = null;

            // Remove or find the card in existing lists
            const updatedActionLists = prevBoard.actionLists.map((list) => {
                // Safety check: ensure list.cards exists
                const listCards = list.cards || [];

                if (cardAction === 'MOVE' && listCards.some((c) => c._id === card._id)) {
                    return {
                        ...list,
                        cards: listCards.filter((c) => {
                            if (c._id === card._id) {
                                cardToProcess = { ...c, actionList: { _id: targetListId } };
                                return false;
                            }
                            return true;
                        }),
                    };
                } else if (list._id === card?.actionList?._id) {
                    cardToProcess = { ...card, actionList: { _id: targetListId } };
                }
                return list;
            });

            if (!cardToProcess) return prevBoard;

            // Add the card to the target list
            const finalActionLists = updatedActionLists.map((list) => {
                if (list._id === targetListId) {
                    // Safety check: ensure list.cards exists
                    const newCards = [...(list.cards || [])];
                    const position = Math.min(targetPosition, newCards.length);
                    newCards.splice(position, 0, cardToProcess);

                    return {
                        ...list,
                        cards: newCards.map((c, index) => ({ ...c, order: index })),
                    };
                }
                return list;
            });

            return { ...prevBoard, actionLists: finalActionLists };
        });
    }, [targetListId, targetPosition, card, cardAction, setBoard]);

    return (
        <div id="zoobbe-board">
            <Board board={boardState} setBoard={setBoard} />
            {isCardPath && <CardDetails board={boardState} setBoard={setBoard} isReadOnly={boardPermissions.isReadOnly} />}
        </div>
    );
}

export default BoardWrapper;
